import { useEffect, useState } from 'react';
import { api } from '@/utils/api';

// Convert hex to HSL for CSS variables
const hexToHSL = (hex) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const ThemeProvider = ({ children }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    loadTheme();
    
    // Reload theme every 30 seconds to catch updates
    const interval = setInterval(loadTheme, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadTheme = async () => {
    try {
      const content = await api.getContent();
      const colors = content?.branding?.colors;
      
      if (colors) {
        const root = document.documentElement;
        
        // Apply colors as CSS custom properties
        if (colors.primary) root.style.setProperty('--primary', hexToHSL(colors.primary));
        if (colors.primary_foreground) root.style.setProperty('--primary-foreground', hexToHSL(colors.primary_foreground));
        if (colors.secondary) root.style.setProperty('--secondary', hexToHSL(colors.secondary));
        if (colors.secondary_foreground) root.style.setProperty('--secondary-foreground', hexToHSL(colors.secondary_foreground));
        if (colors.accent) root.style.setProperty('--accent', hexToHSL(colors.accent));
        if (colors.accent_foreground) root.style.setProperty('--accent-foreground', hexToHSL(colors.accent_foreground));
        if (colors.background) root.style.setProperty('--background', hexToHSL(colors.background));
        if (colors.foreground) root.style.setProperty('--foreground', hexToHSL(colors.foreground));
        if (colors.card) root.style.setProperty('--card', hexToHSL(colors.card));
        if (colors.card_foreground) root.style.setProperty('--card-foreground', hexToHSL(colors.card_foreground));
        if (colors.muted) root.style.setProperty('--muted', hexToHSL(colors.muted));
        if (colors.muted_foreground) root.style.setProperty('--muted-foreground', hexToHSL(colors.muted_foreground));
        if (colors.border) root.style.setProperty('--border', hexToHSL(colors.border));
        if (colors.border) root.style.setProperty('--input', hexToHSL(colors.border));
        if (colors.primary) root.style.setProperty('--ring', hexToHSL(colors.primary));
        if (colors.success) root.style.setProperty('--success', hexToHSL(colors.success));
        if (colors.destructive) root.style.setProperty('--destructive', hexToHSL(colors.destructive));
      }
      
      setLoaded(true);
    } catch (error) {
      console.error('Error loading theme:', error);
      setLoaded(true);
    }
  };

  // Expose refresh function globally for immediate updates
  useEffect(() => {
    window.refreshTheme = loadTheme;
    return () => { delete window.refreshTheme; };
  }, []);

  return children;
};

export default ThemeProvider;
